<template>
    <div class="business-portal">
        <NuxtLoadingIndicator />
        <DynamicDialog />

        <AppConfirmDialog />

        <SkipLink :inert="drawerIsOpen" />

        <AppSidebar />

        <!-- The drawer declares itself `aria-modal` on mobile and traps Tab, but a
             trap only governs the keyboard: browse-mode navigation still walks the
             page behind it, which would make the modal claim false. The whole shell
             is inerted rather than <main> alone because the drawer's own toggle sits
             in .bp-shell__mobile-bar, a sibling of <main>. -->
        <div class="bp-shell" :class="{ mobile: isMobile }" :inert="drawerIsOpen">
            <!-- Classic UI Switch Banner — full-width across the content area -->
            <div class="bp-classic-banner">
                <div class="bp-classic-banner__content">
                    <span class="bp-classic-banner__badge">NEW</span>
                    <span class="bp-classic-banner__text">You're using the new Business Portal.</span>
                    <button class="bp-classic-banner__link" @click="switchToClassicUI">Switch to classic view</button>
                </div>
            </div>
            <div class="bp-shell__mobile-bar">
                <MobileMenuButton />
            </div>
            <main id="main-content" tabindex="-1" class="bp-shell__main">
                <NuxtPage />
            </main>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import DynamicDialog from 'primevue/dynamicdialog';
import AppConfirmDialog from '~/components/layout/AppConfirmDialog.vue';
import AppSidebar from '~/components/layout/Sidebar/AppSidebar.vue';
import SkipLink from '~/components/layout/SkipLink.vue';
import { setBusinessUiPreference } from '~/services/business-ui/preference';
import MobileMenuButton from '~/components/layout/Person/MobileMenuButton.vue';
import { useMobileStore } from '~/stores/layout/mobile';

const route = useRoute();
const config = useRuntimeConfig();

const slug = computed(() => route.params.slug as string);

function switchToClassicUI() {
    setBusinessUiPreference('classic');
    const oldUIUrl = config.public.pb?.appDomainLegacy || 'https://app.privacybee.com';
    window.location.href = `${oldUIUrl}/business/${slug.value}/dashboard`;
}

// One mobile source, shared with default.vue and AppSidebar: a hand-rolled
// `<= 1024` disagreed with useIsMobile's `(max-width: 1023.98px)` at exactly
// 1024px, where the drawer claimed aria-modal over a page that stayed reachable.
const { isMobile } = useIsMobile();
const mobileStore = useMobileStore();
const drawerIsOpen = computed(() => isMobile.value && mobileStore.isMobileNavOpen);

// Deliberately does NOT write the business-UI preference on mount: landing here
// must not erase a staff member's choice of the classic app. The preference is
// only ever set by an explicit switch (the banner below, or ?newui= on the way
// in). useIsMobile owns the resize listener, so there is nothing to clean up.
</script>

<style lang="scss" scoped>
.business-portal {
    background: var(--canvas);
    min-height: 100vh;
}

.bp-shell {
    margin-left: 280px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;

    &.mobile {
        margin-left: 0;
    }

    @media screen and (max-width: 1023.98px) {
        margin-left: 0;
    }

    // Mobile-only strip that surfaces the drawer toggle; the desktop sidebar is
    // always visible, so there is no header band above the content on desktop.
    &__mobile-bar {
        display: none;
        align-items: center;
        padding: 12px 16px;

        @media screen and (max-width: 1023.98px) {
            display: flex;
        }
    }

    &__main {
        // `flex: 1` (== flex: 1 1 0%) was making the column grow from zero on first paint,
        // which combined with the margin-left transition produced the "starts narrow then
        // expands to 1200px" flash on initial load. Use `width: 100%` only with `max-width`
        // for an immediate, stable 1200px frame.
        min-width: 0;
        width: 100%;
        max-width: 1200px;
        margin-left: auto;
        margin-right: auto;
    }
}

.bp-classic-banner {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 24px;
    background: var(--info-wash);
    border-bottom: 1px solid rgba(37, 99, 235, 0.2);
    width: 100%;

    &__content {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    &__badge {
        padding: 2px 8px;
        font-size: 12px;
        font-weight: 700;
        border-radius: 6px;
        background: var(--action);
        color: var(--ink-invert);
    }

    &__text {
        font-size: 14px;
        color: var(--info-ink);
        font-weight: 500;
    }

    &__link {
        font-size: 14px;
        color: var(--action-ink);
        text-decoration: underline;
        cursor: pointer;
        background: transparent;
        border: 0;
        font-weight: 500;
        font-family: inherit;

        &:hover {
            text-decoration: none;
        }
    }
}
</style>
